import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Leaf, MapPin, Heart, Shield, Users } from "lucide-react";
import { products } from "@/data/products";
import { farms } from "@/data/farms";
import { ProductCard } from "@/components/product/ProductCard";
import { FarmCard } from "@/components/farm/FarmCard";

export default function HomePage() {
  const featuredProducts = products.slice(0, 6);
  const featuredFarms = farms.slice(0, 3);

  const steps = [
    {
      number: "01",
      title: "Collect",
      description: "We partner with Dutch farms to collect wool that would otherwise be discarded after annual shearing."
    },
    {
      number: "02",
      title: "Process Locally",
      description: "Wool is sorted, cleaned, and processed at facilities in the Netherlands and Germany."
    },
    {
      number: "03",
      title: "Craft Products",
      description: "Skilled craftsmen transform the wool into premium sweaters, blankets, and accessories."
    },
    {
      number: "04",
      title: "Document Journey",
      description: "Every step is recorded for full traceability from farm to finished product."
    },
    {
      number: "05",
      title: "Tell the Story",
      description: "Each product comes with its complete story—the farm, the farmers, the process."
    }
  ];

  const trustElements = [
    {
      icon: MapPin,
      title: "100% Local Production",
      description: "Every product made in the Netherlands or Germany, supporting local economies."
    },
    {
      icon: Users,
      title: "Fair Farmer Compensation",
      description: "We pay above-market rates for wool that farmers previously couldn't sell."
    },
    {
      icon: Leaf,
      title: "Measurable Waste Reduction",
      description: "Each product saves 45-280g of wool from being discarded or burned."
    },
    {
      icon: Shield,
      title: "Full Traceability",
      description: "Trace your product from sheep to sweater with verified documentation."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/homepage_background.jpg"
            alt="WoolStory lifestyle"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-background/80 via-background/35 to-background/0" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            {/* Badge */}
            <Badge variant="secondary" className="mb-6 bg-secondary/80">
              <Leaf className="h-3 w-3 mr-1" />
              Sustainable Dutch Wool
            </Badge>

            {/* Value Proposition */}
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground leading-tight">
              Premium wool products with a story you can trace.
            </h1>

            {/* Problem → Solution */}
            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-destructive mt-2.5 shrink-0" />
                <p className="text-lg text-muted-foreground">
                  <span className="font-medium text-foreground">The problem:</span> Dutch sheep produce millions of kilos of wool yearly, but most is discarded as waste due to limited processing capacity.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-success mt-2.5 shrink-0" />
                <p className="text-lg text-muted-foreground">
                  <span className="font-medium text-foreground">Our solution:</span> We transform this discarded wool into beautiful, traceable products—processed entirely within NL/DE, with full transparency from farm to finished piece.
                </p>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap gap-4">
              <Button asChild size="lg" className="btn-premium">
                <Link to="/products">
                  Explore products
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/sustainability">
                  How traceability works
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-28 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              From discarded fleece to cherished piece—every step documented.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <Card key={index} className="relative bg-background border-border/50">
                <CardContent className="p-6">
                  <span className="font-display text-4xl font-light text-primary/30">
                    {step.number}
                  </span>
                  <h3 className="font-display text-lg font-semibold text-foreground mt-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {step.description}
                  </p>
                </CardContent>
                {/* Connector Line (hidden on last item and mobile) */}
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px bg-border" />
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
                Featured Products
              </h2>
              <p className="mt-2 text-lg text-muted-foreground">
                Each piece carries the story of its origin.
              </p>
            </div>
            <Button asChild variant="outline" className="hidden sm:flex gap-1">
              <Link to="/products">
                View all
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Button asChild variant="outline" className="gap-1">
              <Link to="/products">
                View all products
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Farms */}
      <section className="py-20 lg:py-28 bg-secondary/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-12">
            <div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
                Meet Our Farms
              </h2>
              <p className="mt-2 text-lg text-muted-foreground">
                The families and flocks behind WoolStory.
              </p>
            </div>
            <Button asChild variant="outline" className="hidden sm:flex gap-1">
              <Link to="/farm-stories">
                All farm stories
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {featuredFarms.map((farm) => (
              <FarmCard key={farm.id} farm={farm} />
            ))}
          </div>

          <div className="mt-8 text-center sm:hidden">
            <Button asChild variant="outline" className="gap-1">
              <Link to="/farm-stories">
                All farm stories
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Trust Elements */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-display text-3xl sm:text-4xl font-semibold text-foreground">
              Why WoolStory
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Concrete commitments, not vague promises.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustElements.map((element, index) => (
              <Card key={index} className="bg-secondary/30 border-border/50">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <element.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-display text-lg font-semibold text-foreground">
                    {element.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {element.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-28 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold">
            Wear a story worth telling.
          </h2>
          <p className="mt-4 text-lg opacity-90 max-w-2xl mx-auto">
            Every WoolStory product transforms discarded Dutch wool into something beautiful—with complete transparency from farm to wardrobe.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" variant="secondary" className="btn-premium">
              <Link to="/products">
                Shop Now
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground bg-transparent hover:bg-primary-foreground/10">
              <Link to="/farm-stories">
                Meet the Farmers
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
